import { z } from "zod";
import type { Address, ContactData } from "./contact.js";
import {
  ASSIGNMENT_TARGETS,
  type AssignmentTarget,
  type BillingState,
  type ConnectionMethod,
  type ConnectionStatus,
  DELIVERY_MODES,
  type DeliveryMode,
  LANGUAGES,
  type Language,
  LIST_COLORS,
  type ListColor,
  OWN_APP_CREDENTIAL_KINDS,
  type OwnAppCheckKey,
  type OwnAppCredentialKind,
  type RecipientReason,
  type RecipientStatus,
  ROLES,
  type Role,
  type SubscriptionStatus,
  type SyncTrigger,
} from "./enums.js";
import { IMPORT_LIMITS } from "./import.js";

/* ---------- Session ---------- */

export type MeResponse = {
  identity: { id: string; name: string; email: string; tenantId: string };
  org: OrgSummary | null;
  role: Role | null;
  /** A Kontaktfix operator works in this organization from the operator dashboard. */
  supportMode: boolean;
  /** Set when the signed-in tenant has no organization yet (onboarding). */
  canCreateOrg: boolean;
  /** Set when an organization exists for the tenant but the user is not a member. */
  orgExistsWithoutAccess: boolean;
  /** Set when an IT partner technician works in the organization (opened from the partner portal). */
  partner: { name: string } | null;
};

export type OrgSummary = {
  id: string;
  name: string;
  tenantId: string;
  language: Language;
  connectionStatus: ConnectionStatus;
  deliveryMode: DeliveryMode;
  /** Suspended by Kontaktfix: members cannot work in it and nothing is delivered. */
  suspended: boolean;
  billing: BillingStatus;
};

/* ---------- Billing ---------- */

/** What the app needs on every page: the banner and whether contacts can be changed. */
export type BillingStatus = {
  state: BillingState;
  /** False in "expired" and "canceled": nothing is delivered, contacts are read-only. */
  canUse: boolean;
  trialEndsAt: string | null;
};

export type BillingOverview = BillingStatus & {
  /** People who receive contacts now (see services/billing.ts for who counts). */
  billableUsers: number;
  pricePerUser: number;
  currency: string;
  /** billableUsers x pricePerUser, excluding VAT. */
  estimatedMonthly: number;
  /** From this many users prices are on request. */
  volumeFromUsers: number;
  trialDays: number;
  subscription: {
    status: SubscriptionStatus;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    /** Users charged on the subscription right now (updated daily). */
    quantity: number | null;
  } | null;
  /** Checkout and the billing portal work (Stripe configured). */
  checkoutAvailable: boolean;
  /** The organization has a Stripe customer, so the billing portal (invoices, payment method) opens. */
  portalAvailable: boolean;
  /** An IT partner pays for the organization (issue #27); endsAt: partner billing ends then. */
  partner: { name: string; endsAt: string | null } | null;
};

/* ---------- Organization settings ---------- */

export type OrgSettings = {
  id: string;
  name: string;
  tenantId: string;
  language: Language;
  deliveryMode: DeliveryMode;
  folderName: string;
  noteText: string;
  categoriesEnabled: boolean;
  connection: {
    status: ConnectionStatus;
    method: ConnectionMethod;
    checkedAt: string | null;
    error: string | null;
    consentUrlAvailable: boolean;
    /** The server can store credentials of a customer-owned app (encryption key configured). */
    ownAppAvailable: boolean;
    /** Application (client) ID Kontaktfix uses in this tenant: ours, or the customer's own app. */
    clientId: string | null;
    /** Set when the organization connects with its own app registration. */
    ownApp: {
      clientId: string;
      kind: OwnAppCredentialKind;
      secretHint: string | null;
      certificateExpiresAt: string | null;
    } | null;
    /** Set while Kontaktfix removes delivered contacts before disconnecting. */
    disconnecting: { startedAt: string; mailboxesLeft: number } | null;
    /** Microsoft 365 change notifications (issue #31); "users_only" until Group.Read.All is approved. */
    notifications: "active" | "users_only" | "unavailable" | null;
  };
};

/* ---------- Own app registration (service principal in the customer's tenant) ---------- */

/** Everything the setup page needs to create the app registration. */
export type OwnAppSetup = {
  tenantId: string;
  orgName: string;
  /** Public certificate Kontaktfix generated for this organization. The private key never leaves the server. */
  certificate: {
    /** Base64 DER, as uploaded to Entra (and embedded in the setup script). */
    base64: string;
    pem: string;
    thumbprint: string;
    expiresAt: string;
  };
  /** The registration currently in use, if any. */
  active: { clientId: string; kind: OwnAppCredentialKind; secretHint: string | null } | null;
};

export const ownAppVerifySchema = z
  .object({
    clientId: z.string().trim().uuid(),
    kind: z.enum(OWN_APP_CREDENTIAL_KINDS),
    /** Required for "secret", unless the same app is already connected with a stored secret. */
    secret: z.string().trim().min(1).max(512).optional(),
  })
  .strict();
export type OwnAppVerifyInput = z.infer<typeof ownAppVerifySchema>;

export type OwnAppCheckStatus = "ok" | "failed" | "skipped";

/**
 * Result of one check. `reason` is machine-readable and translated by the UI:
 * app_not_found, invalid_secret, secret_expired, certificate_not_uploaded, kontaktfix_app,
 * tenant_not_found, not_granted, not_effective, unknown.
 */
export type OwnAppCheck = {
  key: OwnAppCheckKey;
  status: OwnAppCheckStatus;
  reason: string | null;
  detail: string | null;
};

export type OwnAppVerifyResult = {
  ok: boolean;
  checks: OwnAppCheck[];
  /** Fresh settings when the app was connected. */
  settings: OrgSettings | null;
};

export const orgSettingsUpdateSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  language: z.enum(LANGUAGES).optional(),
  deliveryMode: z.enum(DELIVERY_MODES).optional(),
  folderName: z.string().trim().min(1).max(100).optional(),
  noteText: z.string().trim().max(500).optional(),
  categoriesEnabled: z.boolean().optional(),
});
export type OrgSettingsUpdate = z.infer<typeof orgSettingsUpdateSchema>;

const utm = z.string().trim().max(200).optional();

/** UTM parameters of the link that brought the person to the app (kept in the tab, no cookie). */
export const attributionSchema = z.object({
  source: utm,
  medium: utm,
  campaign: utm,
  term: utm,
  content: utm,
});
export type Attribution = z.infer<typeof attributionSchema>;

export const createOrgSchema = z.object({
  name: z.string().trim().min(1).max(200),
  language: z.enum(LANGUAGES),
  attribution: attributionSchema.optional(),
});

/* ---------- Members ---------- */

export type Member = {
  id: string;
  objectId: string;
  name: string;
  email: string;
  role: Role;
  lastLoginAt: string | null;
  createdAt: string;
  listIds: string[];
  /** Name of the IT partner for partner technicians (issue #27). */
  partnerName: string | null;
};

export const memberCreateSchema = z.object({
  objectId: z.string().min(1),
  role: z.enum(ROLES),
});
export const memberUpdateSchema = z.object({ role: z.enum(ROLES) });

/* ---------- Contacts ---------- */

export type Contact = ContactData & {
  id: string;
  displayName: string;
  hasPhoto: boolean;
  photoVersion: string | null;
  listIds: string[];
  createdAt: string;
  updatedAt: string;
  /** True when the current user may edit this contact. */
  canEdit: boolean;
};

export type ContactListItem = {
  id: string;
  displayName: string;
  companyName: string | null;
  jobTitle: string | null;
  email1: string | null;
  primaryPhone: string | null;
  hasPhoto: boolean;
  photoVersion: string | null;
  listIds: string[];
  updatedAt: string;
  canEdit: boolean;
};

export type Paged<T> = { items: T[]; total: number };

/**
 * Which contacts a bulk action applies to: explicit ids, or "all matching" the contacts filter
 * minus the rows unchecked again (issue #56). `expectedCount` is the number the user confirmed;
 * the server refuses with 409 selection_changed when the data changed in between.
 */
export const contactSelectionSchema = z.union([
  z.object({ contactIds: z.array(z.string().uuid()).min(1).max(5000) }),
  z.object({
    filter: z.object({ q: z.string().max(200).optional(), listId: z.string().uuid().optional() }),
    exceptIds: z.array(z.string().uuid()).max(5000).default([]),
    expectedCount: z.number().int().min(1),
  }),
]);
export type ContactSelection = z.input<typeof contactSelectionSchema>;

export const bulkListMembershipSchema = z.object({ listId: z.string().uuid() }).and(contactSelectionSchema);

/* ---------- Lists ---------- */

/**
 * Directory lists (issue #26): the contacts are employees from Microsoft 365, refreshed with the
 * directory. Microsoft 365 is the only source; Kontaktfix only chooses who and which fields.
 */
export const DIRECTORY_FIELDS = [
  "jobTitle",
  "department",
  "companyName",
  "officeLocation",
  "email",
  "businessPhones",
  "mobilePhone",
  "businessAddress",
  "photo",
] as const;
export type DirectoryField = (typeof DIRECTORY_FIELDS)[number];

/** Fields delivered by default. The mobile number is off: it is often a private number (revDSG). */
export const DEFAULT_DIRECTORY_FIELDS: DirectoryField[] = [
  "jobTitle",
  "department",
  "companyName",
  "officeLocation",
  "email",
  "businessPhones",
  "businessAddress",
  "photo",
];

const filterValues = z.array(z.string().trim().min(1).max(200)).max(50).default([]);
/** A group or person from Microsoft 365; the name is only for display. */
const directoryRef = z.object({ id: z.string().min(1).max(100), name: z.string().max(200).default("") });

export const directorySourceSchema = z.object({
  /** Everybody in the tenant, or the (transitive) members of some groups. */
  scope: z.enum(["all", "groups"]),
  groups: z.array(directoryRef).max(20).default([]),
  /** Only people whose attribute matches one of the values (case-insensitive). Empty = no filter. */
  filters: z
    .object({
      companyName: filterValues,
      department: filterValues,
      officeLocation: filterValues,
      country: filterValues,
      /** e.g. "muster.ch": matches the mail address domain. */
      mailDomain: filterValues,
    })
    .default({ companyName: [], department: [], officeLocation: [], country: [], mailDomain: [] }),
  fields: z.array(z.enum(DIRECTORY_FIELDS)).default(DEFAULT_DIRECTORY_FIELDS),
  /** Added on purpose, e.g. a shared mailbox like "Support" (not recognizable through Graph otherwise). */
  include: z.array(directoryRef).max(200).default([]),
  /** Never delivered, e.g. management or people with a private number in Microsoft 365. */
  exclude: z.array(directoryRef).max(1000).default([]),
});
export type DirectorySource = z.output<typeof directorySourceSchema>;

/** Why a person of the source is not on a directory list. */
export const DIRECTORY_EXCLUSION_REASONS = [
  "guest",
  "disabled",
  "no_mail",
  "hidden",
  "filtered",
  "excluded",
] as const;
export type DirectoryExclusion = {
  id: string;
  displayName: string;
  reason: (typeof DIRECTORY_EXCLUSION_REASONS)[number];
};

export type ContactList = {
  id: string;
  name: string;
  description: string | null;
  color: ListColor;
  /** "directory": filled from Microsoft 365, "file": from a scheduled file. Contacts are read-only. */
  kind: "manual" | "directory" | "file";
  directory:
    | (DirectorySource & {
        refreshedAt: string | null;
        error: string | null;
        /** People of the source not on the list (except "filtered", which can be many). */
        excluded: DirectoryExclusion[];
      })
    | null;
  file: FileListState | null;
  contactCount: number;
  recipientCount: number;
  assignmentCount: number;
  editorIds: string[];
  updatedAt: string;
  canEdit: boolean;
};

export const listInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).optional().nullable(),
  color: z.enum(LIST_COLORS).default("iris"),
  /** Only on create: a list keeps its kind. Updates may change `directory` or `file`. */
  kind: z.enum(["manual", "directory", "file"]).default("manual"),
  directory: directorySourceSchema.nullish(),
  /** Defined at the end of this file (it needs IMPORT_TARGETS), hence lazy. */
  file: z.lazy(() => fileSourceSchema).nullish(),
  /** New password, token or private key for the file; omitted keeps the stored one. */
  fileSecret: z.string().max(20_000).optional(),
});

export const listEditorsSchema = z.object({ memberIds: z.array(z.string().uuid()) });

/* ---------- Assignments and directory ---------- */

export type Assignment = {
  id: string;
  listId: string;
  targetType: AssignmentTarget;
  targetId: string;
  displayName: string;
  detail: string | null;
  memberCount: number | null;
  missing: boolean;
  createdAt: string;
};

export const assignmentCreateSchema = z.object({
  targetType: z.enum(ASSIGNMENT_TARGETS),
  targetId: z.string().min(1),
});

/** What an assignment would change, shown before saving it (issue #33). */
export type AssignmentImpact = {
  /** People in the assigned user or group (all of them receive the list). */
  recipients: number;
  /** Of those, people who receive nothing yet (new billable users). */
  newRecipients: number;
  /** Highest number of contacts one of these people would receive. */
  maxContactsPerRecipient: number;
  /** Contacts written to mailboxes for this change (new contacts per person, summed). */
  writes: number;
  estimate: { minMinutes: number; maxMinutes: number };
  limits: { perRecipient: number; perRecipientWarning: number };
};

export type DirectoryEntry = {
  type: AssignmentTarget;
  id: string;
  displayName: string;
  detail: string | null;
};

/* ---------- People (recipients) ---------- */

export type RecipientSource = {
  listId: string;
  via: { type: "direct" } | { type: "group"; groupId: string; groupName: string };
};

export type Recipient = {
  userId: string;
  displayName: string;
  email: string | null;
  status: RecipientStatus;
  reason: RecipientReason | null;
  reasonDetail: string | null;
  contactCount: number;
  listCount: number;
  lastSyncedAt: string | null;
};

export type RecipientDetail = Recipient & { sources: RecipientSource[] };

/** Read-only CardDAV access of one person (issue #29). The password is only in CarddavCreated. */
export type CarddavAccess = {
  url: string;
  access: { username: string; createdAt: string; lastUsedAt: string | null } | null;
};
export type CarddavCreated = {
  url: string;
  username: string;
  password: string;
  /** iOS/macOS configuration profile (.mobileconfig) with the password, for download or MDM. */
  mobileconfig: string;
};

/* ---------- Sync ---------- */

export type SyncOverview = {
  lastSuccessAt: string | null;
  totals: Record<RecipientStatus, number>;
  recipientCount: number;
  deliveredCount: number;
  running: boolean;
  connectionStatus: ConnectionStatus;
  runs: SyncRun[];
  problems: Recipient[];
};

export type SyncRun = {
  id: string;
  trigger: SyncTrigger;
  /** "cancelled": an administrator or operator stopped it. */
  status: "running" | "done" | "failed" | "cancelled";
  startedAt: string;
  finishedAt: string | null;
  recipientsTotal: number;
  recipientsDone: number;
  created: number;
  updated: number;
  deleted: number;
  errors: number;
  /** Mailboxes by state, for precise progress. */
  mailboxes: {
    queued: number;
    running: number;
    done: number;
    failed: number;
    superseded: number;
    cancelled: number;
  };
  /** Writes planned by the mailboxes that started, and how many of them are processed. */
  planned: number;
  processed: number;
  /** False for runs from before per-mailbox tracking (only totals are known). */
  detailed: boolean;
};

export type SyncRunMailboxStatus = "queued" | "running" | "done" | "failed" | "superseded" | "cancelled";

export type SyncRunMailbox = {
  userId: string;
  displayName: string;
  email: string | null;
  status: SyncRunMailboxStatus;
  plan: { create: number; update: number; delete: number };
  done: { created: number; updated: number; deleted: number; errors: number; photos: number };
  /** HTTP calls to Microsoft (one per batch of up to 20 writes) and time waited on throttling. */
  calls: number;
  throttledSeconds: number;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
};

export type SyncRunDetail = { run: SyncRun; mailboxes: SyncRunMailbox[] };

export type SyncChangeOp = "create" | "update" | "delete";

export type SyncRunChange = {
  contactId: string | null;
  label: string;
  op: SyncChangeOp;
  /** update: "changed" | "edited_in_outlook"; delete: "not_desired" | "duplicate" | "orphan" | "moved". */
  reason: string | null;
  /** Mailboxes where the change was written, and where it failed. */
  mailboxes: number;
  errors: number;
};

export type SyncRunChanges = {
  total: number;
  counts: Record<SyncChangeOp, number>;
  items: SyncRunChange[];
};

export type SyncRunEvent = {
  id: number;
  at: string;
  level: "info" | "warn" | "error";
  code: string;
  userId: string | null;
  displayName: string | null;
  data: Record<string, string | number | null> | null;
};

/**
 * Progress of a run: share of writes processed. Mailboxes that have not started yet are counted
 * with the average plan of those that did, so the bar does not jump when they start.
 */
export function runProgress(run: SyncRun): { percent: number; remainingMinutes: number | null } {
  const m = run.mailboxes;
  const total = m.queued + m.running + m.done + m.failed + m.superseded + m.cancelled;
  const finished = m.done + m.failed + m.superseded + m.cancelled;
  if (run.status === "done") return { percent: 100, remainingMinutes: null };
  if (run.status !== "running") {
    // A failed or stopped run shows how far it got, not a full bar.
    const percent = run.planned
      ? Math.floor((run.processed / run.planned) * 100)
      : total
        ? Math.floor(((m.done + m.superseded) / total) * 100)
        : 0;
    return { percent: Math.min(100, percent), remainingMinutes: null };
  }
  const started = m.running + m.done + m.failed;
  const average = started ? run.planned / started : 0;
  const expected = run.planned + average * m.queued;
  const percent = expected
    ? Math.min(99, Math.floor((run.processed / expected) * 100))
    : total
      ? Math.floor((finished / total) * 100)
      : 0;
  const elapsed = (Date.now() - new Date(run.startedAt).getTime()) / 60_000;
  const rate = elapsed > 0.5 ? run.processed / elapsed : 0;
  const remainingMinutes =
    rate > 0 && expected ? Math.max(1, Math.ceil((expected - run.processed) / rate)) : null;
  return { percent, remainingMinutes };
}

/* ---------- Activity ---------- */

export type AuditEntry = {
  id: string;
  createdAt: string;
  actorName: string;
  actorKind: "user" | "support" | "system";
  entityType: string;
  entityId: string | null;
  entityLabel: string | null;
  action: string;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  meta: Record<string, unknown> | null;
};

/* ---------- Import ---------- */

export type ImportParseResult =
  | {
      kind: "table";
      columns: string[];
      rows: string[][];
      suggestedMapping: Record<string, ImportTarget | null>;
    }
  | { kind: "contacts"; contacts: ImportContactRaw[] };

export const IMPORT_TARGETS = [
  "title",
  "givenName",
  "middleName",
  "surname",
  "fullName",
  "nickName",
  "companyName",
  "department",
  "jobTitle",
  "profession",
  "officeLocation",
  "manager",
  "assistantName",
  "imAddress",
  "email1",
  "email2",
  "email3",
  "businessPhone1",
  "businessPhone2",
  "mobilePhone",
  "homePhone1",
  "homePhone2",
  "businessStreet",
  "businessPostalCode",
  "businessCity",
  "businessState",
  "businessCountry",
  "homeStreet",
  "homePostalCode",
  "homeCity",
  "homeCountry",
  "website",
  "notes",
] as const;
export type ImportTarget = (typeof IMPORT_TARGETS)[number];

/** A contact as read from a file, before validation. Photo is base64 JPEG/PNG when present. */
export type ImportContactRaw = {
  title?: string;
  givenName?: string;
  middleName?: string;
  surname?: string;
  nickName?: string;
  companyName?: string;
  department?: string;
  jobTitle?: string;
  profession?: string;
  officeLocation?: string;
  manager?: string;
  assistantName?: string;
  imAddress?: string;
  email1?: string;
  email2?: string;
  email3?: string;
  businessPhone1?: string;
  businessPhone2?: string;
  mobilePhone?: string;
  homePhone1?: string;
  homePhone2?: string;
  businessAddress?: Partial<Address> | null;
  homeAddress?: Partial<Address> | null;
  otherAddress?: Partial<Address> | null;
  website?: string;
  notes?: string;
  photoBase64?: string;
};

export type ImportPreviewRow = {
  index: number;
  displayName: string;
  data: ContactData | null;
  errors: { field: string; message: string }[];
  duplicates: { contactId: string; displayName: string; reason: "email" | "phone" | "name" }[];
  duplicateInFile: number | null;
};

export const importPreviewSchema = z.object({
  contacts: z.array(z.record(z.string(), z.unknown())).max(IMPORT_LIMITS.maxRows),
});

export const importCommitSchema = z.object({
  rows: z
    .array(
      z.object({
        contact: z.record(z.string(), z.unknown()),
        action: z.enum(["create", "update"]),
        targetId: z.string().uuid().optional(),
      }),
    )
    // One chunk of an import (the web app sends IMPORT_LIMITS.commitChunkRows per request).
    .max(IMPORT_LIMITS.commitChunkRows * 2),
  listIds: z.array(z.string().uuid()).default([]),
  /** Groups the chunks of one import in the audit log; chunk 0 counts as the feature use. */
  importId: z.string().uuid().optional(),
  chunk: z.number().int().min(0).optional(),
});

/* ---------- Email notifications ---------- */

/** Email categories (zuvo standard). Transactional emails cannot be turned off. */
export const EMAIL_CATEGORIES = ["transactional", "product", "lifecycle", "updates"] as const;
export type EmailCategory = (typeof EMAIL_CATEGORIES)[number];
export type OptionalEmailCategory = Exclude<EmailCategory, "transactional">;

export type NotificationPreferences = Record<OptionalEmailCategory, boolean> & { email: string };

export const notificationPreferencesSchema = z.object({
  product: z.boolean(),
  lifecycle: z.boolean(),
  updates: z.boolean(),
});

/* ---------- File lists (issue #28) ---------- */

/** How often Kontaktfix fetches the file. */
export const FILE_SCHEDULES = ["hourly", "daily"] as const;
/** What happens to contacts that are no longer in the file. */
export const FILE_DELETIONS = ["after_days", "immediately", "never"] as const;
/** HTTPS: none, basic or bearer. SFTP: password or private key. */
export const FILE_AUTH_TYPES = ["none", "basic", "bearer", "password", "key"] as const;

export const fileSourceSchema = z
  .object({
    url: z
      .string()
      .trim()
      .max(2000)
      .refine((u) => /^(https|sftp):\/\/[^/\s]+/i.test(u), "https_or_sftp"),
    authType: z.enum(FILE_AUTH_TYPES).default("none"),
    username: z.string().trim().max(200).default(""),
    schedule: z.enum(FILE_SCHEDULES).default("daily"),
    /** Column header -> contact field, as in the import (null = ignored). */
    mapping: z.record(z.string().max(200), z.enum(IMPORT_TARGETS).nullable()).default({}),
    /** Column that identifies a contact across fetches (e.g. a customer number). Null: email, else name. */
    keyColumn: z.string().max(200).nullable().default(null),
    deletion: z.enum(FILE_DELETIONS).default("after_days"),
    deletionDays: z.number().int().min(1).max(90).default(7),
  })
  .refine(
    (s) =>
      (s.url.toLowerCase().startsWith("sftp:") ? ["password", "key"] : ["none", "basic", "bearer"]).includes(
        s.authType,
      ),
    {
      message: "auth_type_mismatch",
      path: ["authType"],
    },
  );
export type FileSource = z.output<typeof fileSourceSchema>;

export type FileListStats = {
  rows: number;
  created: number;
  updated: number;
  removed: number;
  /** Contacts missing from the file that are removed later (deletion "after_days"). */
  missing: number;
  invalid: number;
  duplicates: number;
};

export type FileListState = FileSource & {
  /** A password, token or key is stored (it is never sent back). */
  hasSecret: boolean;
  refreshedAt: string | null;
  error: string | null;
  stats: FileListStats | null;
};

/** Reads the file once without saving, to set up the column mapping. */
export const filePreviewSchema = z.object({
  file: fileSourceSchema,
  /** New password, token or private key; omitted: the stored one of `listId`. */
  secret: z.string().max(20_000).optional(),
  listId: z.string().uuid().optional(),
});

export type FilePreview = {
  columns: string[];
  rows: string[][];
  total: number;
  suggestedMapping: Record<string, ImportTarget | null>;
};

/* ---------- Integrations (issue #34) ---------- */

/** Connectors built when a customer asks; the settings show them with an "on request" button. */
export const ON_REQUEST_INTEGRATIONS = [
  "bexio",
  "abacus",
  "sage",
  "hubspot",
  "pipedrive",
  "dynamics",
  "zapier",
] as const;
export type OnRequestIntegration = (typeof ON_REQUEST_INTEGRATIONS)[number];
export const integrationRequestSchema = z.object({ integration: z.enum(ON_REQUEST_INTEGRATIONS) });
